import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Hotel, Users, Building, DollarSign, TrendingUp, Activity,
  ArrowUpRight, Bed, CalendarCheck, UtensilsCrossed
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function SuperAdminOverview() {
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { openTab } = useTabs();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes] = await Promise.all([
        api.get('/super-admin/analytics')
      ]);
      setAnalytics(analyticsRes.data);
      
      // Generate some activity from analytics if available
      if (analyticsRes.data) {
        const activity = [];
        if (analyticsRes.data.total_hotels > 0) {
          activity.push({ type: 'hotels', count: analyticsRes.data.total_hotels, label: 'Hotels registered' });
        }
        if (analyticsRes.data.total_users > 0) {
          activity.push({ type: 'users', count: analyticsRes.data.total_users, label: 'Total users' });
        }
        if (analyticsRes.data.active_hotels > 0) {
          activity.push({ type: 'active', count: analyticsRes.data.active_hotels, label: 'Active hotels' });
        }
        setRecentActivity(activity);
      }
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="super-admin-overview-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Hotels',
      value: analytics?.total_hotels || 0,
      icon: Hotel,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      description: 'Registered on platform'
    },
    {
      title: 'Active Hotels',
      value: analytics?.active_hotels || 0,
      icon: Building,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      description: 'Currently operating'
    },
    {
      title: 'Total Users',
      value: analytics?.total_users || 0,
      icon: Users,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      description: 'Across all hotels'
    },
    {
      title: 'Platform Revenue',
      value: `₹${(analytics?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      description: 'Total revenue generated'
    },
  ];

  const quickActions = [
    {
      title: 'Manage Hotels',
      description: 'View, edit and manage all hotels',
      icon: Hotel,
      color: 'bg-blue-50 border-blue-100 hover:border-blue-300',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      onClick: () => navigate('/admin/super-admin/dashboard')
    },
    {
      title: 'User Management',
      description: 'Manage users across all hotels',
      icon: Users,
      color: 'bg-purple-50 border-purple-100 hover:border-purple-300',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      onClick: () => navigate('/admin/super-admin/users')
    },
    {
      title: 'Role Settings',
      description: 'Configure user roles and permissions',
      icon: Activity,
      color: 'bg-green-50 border-green-100 hover:border-green-300',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      onClick: () => navigate('/admin/settings/roles')
    },
    {
      title: 'SMTP Configuration',
      description: 'Setup email service',
      icon: TrendingUp,
      color: 'bg-amber-50 border-amber-100 hover:border-amber-300',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      onClick: () => navigate('/admin/settings/smtp')
    },
  ];

  return (
    <div className="space-y-8" data-testid="super-admin-overview">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-gray-800" style={{ fontFamily: 'Manrope' }}>
            Super Admin <span className="text-gradient-warm">Dashboard</span>
          </h1>
          <p className="text-gray-500 mt-2">Platform-wide overview and management</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => openTab({
              type: TAB_TYPES.PAGE_CREATE_HOTEL,
              id: `create-hotel-${Date.now()}`,
              title: 'Add New Hotel',
              data: {}
            })}
            className="bg-gradient-orange hover:opacity-90"
            data-testid="add-hotel-btn"
          >
            <Hotel className="h-4 w-4 mr-2" />
            Add New Hotel
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="bg-white border-orange-100 hover:border-orange-200 card-hover overflow-hidden shadow-sm"
              data-testid={`overview-stat-${index}`}
            >
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2">
                      {stat.title}
                    </p>
                    <p className="text-3xl font-bold tracking-tight text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{stat.description}</p>
                  </div>
                  <div className={`h-10 w-10 rounded-xl ${stat.iconBg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions & Platform Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <Card className="bg-white border-orange-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-gray-800" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={index}
                      onClick={action.onClick}
                      className={`p-4 rounded-xl border ${action.color} transition-all duration-200 text-left group`}
                      data-testid={`quick-action-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg ${action.iconBg} flex items-center justify-center`}>
                            <Icon className={`h-5 w-5 ${action.iconColor}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">{action.title}</p>
                            <p className="text-xs text-gray-500">{action.description}</p>
                          </div>
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Summary */}
        <Card className="bg-white border-orange-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-800" style={{ fontFamily: 'Manrope' }}>Platform Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-purple-50 border border-orange-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-orange flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Platform Status</p>
                  <p className="text-xs text-gray-500">All systems operational</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Hotels Online</span>
                  <Badge className="bg-green-100 text-green-700">
                    {analytics?.active_hotels || 0}/{analytics?.total_hotels || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Active Users</span>
                  <Badge className="bg-purple-100 text-purple-700">
                    {analytics?.total_users || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Rooms</span>
                  <Badge className="bg-blue-100 text-blue-700">
                    {analytics?.total_rooms || 0}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="text-center pt-2">
              <p className="text-xs text-gray-400">
                Last updated: {new Date().toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
